import { PhoneNumberSelector } from '../inbox/PhoneNumberSelector';
import { SMSMessagePreview } from '../inbox/SMSMessagePreview';
import {
  postCommunication,
  communicationError,
  deliveryLabel,
} from '../../lib/communicationRequests';
import type { SMSDelivery } from '../../types/communications';
import React, { useState, useEffect, useRef } from 'react';
import { apiClient } from '../../lib/api';
import Box from '@mui/joy/Box';
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Textarea from '@mui/joy/Textarea';
import Stack from '@mui/joy/Stack';
import Sheet from '@mui/joy/Sheet';
import Chip from '@mui/joy/Chip';
import Avatar from '@mui/joy/Avatar';
import Badge from '@mui/joy/Badge';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import DialogTitle from '@mui/joy/DialogTitle';
import DialogContent from '@mui/joy/DialogContent';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Checkbox from '@mui/joy/Checkbox';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemContent from '@mui/joy/ListItemContent';
import Input from '@mui/joy/Input';

// Icons
import {
  PaperPlaneTilt as SendIcon,
  Envelope as EmailIcon,
  ChatCircle as SmsIcon,
  Bell as NotificationsIcon,
  CaretDown as ExpandMoreIcon,
  CaretUp as ExpandLessIcon,
  ChatCircle as MessageIcon,
  Phone as PhoneIcon,
  Buildings as BusinessIcon,
  User as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Clock as AccessTimeIcon,
  XCircle as ErrorIcon,
} from '@phosphor-icons/react';

import { formatDistanceToNow, format } from 'date-fns';
import type { Job } from '../../types';

interface CommunicationThread {
  id: number;
  job: number;
  job_number: string;
  subject: string;
  thread_type: 'email' | 'sms' | 'notification' | 'phone' | 'video_call' | 'in_person';
  message_count: number;
  last_activity_at: string;
  is_active: boolean;
  created_at: string;
  participants: Participant[];
  recent_message?: {
    id: number;
    content: string;
    message_type: string;
    direction: 'inbound' | 'outbound';
    status: string;
    sender: {
      type: string;
      name: string;
    };
    created_at: string;
  };
  unread_count: number;
}

interface CommunicationMessage {
  sms_deliveries?: SMSDelivery[];
  id: number;
  thread: number;
  thread_subject: string;
  subject: string;
  content: string;
  message_type: 'email' | 'sms' | 'notification' | 'phone' | 'video_call' | 'in_person';
  direction: 'inbound' | 'outbound';
  status: string;
  sent_at?: string;
  delivered_at?: string;
  read_at?: string;
  created_at: string;
  sender_info: {
    type: string;
    id: number;
    name: string;
    email?: string;
  };
  recipients_info: Participant[];
}

interface Participant {
  type: 'contact' | 'user' | 'subcontractor';
  id: number;
  name: string;
  email?: string;
  phone?: string;
}

interface JobParticipants {
  contacts: Participant[];
  users: Participant[];
  subcontractors: Participant[];
}

interface JobCommunicationProps {
  job: Job;
  onSendCommunication?: (data: any) => void;
}

const getMessageTypeIcon = (type: string) => {
  switch (type) {
    case 'email':
      return <EmailIcon />;
    case 'sms':
      return <SmsIcon />;
    case 'notification':
      return <NotificationsIcon />;
    case 'phone':
      return <PhoneIcon />;
    default:
      return <MessageIcon />;
  }
};

const getParticipantIcon = (type: string) => {
  switch (type) {
    case 'contact':
      return <PersonIcon />;
    case 'user':
      return <PersonIcon />;
    case 'subcontractor':
      return <BusinessIcon />;
    default:
      return <PersonIcon />;
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'sent':
    case 'delivered':
    case 'read':
      return <CheckCircleIcon color="var(--joy-palette-success-500)" />;
    case 'sending':
      return <AccessTimeIcon color="var(--joy-palette-warning-500)" />;
    case 'failed':
      return <ErrorIcon color="var(--joy-palette-danger-500)" />;
    default:
      return <AccessTimeIcon />;
  }
};

const JobCommunication: React.FC<JobCommunicationProps> = ({ job, onSendCommunication }) => {
  const [threads, setThreads] = useState<CommunicationThread[]>([]);
  const [participants, setParticipants] = useState<JobParticipants>({
    contacts: [],
    users: [],
    subcontractors: [],
  });
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedThread, setSelectedThread] = useState<CommunicationThread | null>(null);
  const [messages, setMessages] = useState<CommunicationMessage[]>([]);
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [isMessagesOpen, setIsMessagesOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [smsSender, setSmsSender] = useState('');
  const [recipientOverrides, setRecipientOverrides] = useState<Record<string, string>>({});
  const [sendError, setSendError] = useState('');
  const [deliveryResult, setDeliveryResult] = useState<CommunicationMessage | null>(null);
  const [retrying, setRetrying] = useState<number | null>(null);

  // Compose form state
  const [composeData, setComposeData] = useState({
    subject: '',
    content: '',
    message_type: 'email' as 'email' | 'sms' | 'notification',
    thread_id: null as number | null,
    thread_subject: '',
    recipient_contacts: [] as number[],
    recipient_users: [] as number[],
    recipient_subcontractors: [] as number[],
  });

  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    console.log('JobCommunication component mounted for job:', job);
    loadThreads();
    loadParticipants();
  }, [job.id]);

  useEffect(() => {
    console.log('Participants state updated:', participants);
  }, [participants]);

  const loadThreads = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/api/jobs/${job.id}/communication-threads/`);
      setThreads(response.data as CommunicationThread[]);
    } catch (error) {
      console.error('Failed to load communication threads:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadParticipants = async () => {
    try {
      setParticipantsLoading(true);
      console.log('Loading participants for job:', job.id);
      const response = await apiClient.get(`/api/jobs/${job.id}/communication-participants/`);
      console.log('Participants data received:', response.data);
      setParticipants(response.data as JobParticipants);
    } catch (error) {
      console.error('Failed to load participants:', error);
    } finally {
      setParticipantsLoading(false);
    }
  };

  const loadMessages = async (thread: CommunicationThread) => {
    try {
      setLoading(true);
      const response = await apiClient.get(
        `/api/jobs/${job.id}/communication-threads/${thread.id}/messages/`,
      );
      setMessages(response.data as CommunicationMessage[]);
      setSelectedThread(thread);
      setIsMessagesOpen(true);

      // Mark messages as read
      await markThreadAsRead(thread.id);
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const markThreadAsRead = async (threadId: number) => {
    try {
      await apiClient.post(`/api/jobs/${job.id}/communication-threads/${threadId}/mark-read/`, {});

      // Refresh threads to update unread counts
      loadThreads();
    } catch (error) {
      console.error('Failed to mark thread as read:', error);
    }
  };

  const sendCommunication = async () => {
    try {
      setLoading(true);

      // Validate recipients
      const hasRecipients =
        composeData.recipient_contacts.length > 0 ||
        composeData.recipient_users.length > 0 ||
        composeData.recipient_subcontractors.length > 0;

      if (!hasRecipients) {
        alert('Please select at least one recipient');
        return;
      }

      if (!composeData.content.trim()) {
        alert('Please enter a message');
        return;
      }

      if (composeData.message_type === 'sms' && !smsSender) {
        setSendError('Select an activated texting number.');
        return;
      }
      setSendError('');
      const selectedKeys = new Set([
        ...composeData.recipient_contacts.map((id) => `contact:${id}`),
        ...composeData.recipient_users.map((id) => `user:${id}`),
        ...composeData.recipient_subcontractors.map((id) => `subcontractor:${id}`),
      ]);
      const recipient_overrides =
        composeData.message_type === 'notification'
          ? {}
          : Object.fromEntries(
              Object.entries(recipientOverrides).filter(([key]) => selectedKeys.has(key)),
            );
      const response =
        composeData.message_type === 'sms'
          ? await postCommunication<CommunicationMessage>(
              `/api/jobs/${job.id}/send-communication/`,
              { ...composeData, from_number: smsSender, recipient_overrides },
            )
          : await apiClient.post<CommunicationMessage>(`/api/jobs/${job.id}/send-communication/`, {
              ...composeData,
              recipient_overrides,
            });
      const data = response.data;
      if (data.message_type === 'sms') setDeliveryResult(data);

      setRecipientOverrides({});
      // Reset form
      setComposeData({
        subject: '',
        content: '',
        message_type: 'email',
        thread_id: null,
        thread_subject: '',
        recipient_contacts: [],
        recipient_users: [],
        recipient_subcontractors: [],
      });

      setIsComposeOpen(false);

      // Refresh threads
      loadThreads();

      // Call parent callback if provided
      if (onSendCommunication) {
        onSendCommunication(data);
      }
    } catch (error) {
      console.error('Failed to send communication:', error);
      setSendError(communicationError(error));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Open the composer seeded from the job itself.
   *
   * The job's own customer is who these messages go to, and the participant
   * list already knows it — re-ticking the same person on every message was
   * pure re-entry. The thread name beats the backend's dated fallback
   * ("Email - 2026-08-12") once a handful of threads are on screen.
   *
   * Only empty fields are seeded, so a draft in progress is never rewritten.
   */
  const openCompose = () => {
    setComposeData((prev) => ({
      ...prev,
      recipient_contacts:
        prev.recipient_contacts.length > 0
          ? prev.recipient_contacts
          : job.associated_contact
            ? [job.associated_contact.id]
            : [],
      // Capped at the serializer's 255 — a long street address would
      // otherwise turn "New Message" into a 400 the user can't act on.
      thread_subject:
        prev.thread_subject || `Job ${job.job_number} - ${job.address_1}`.slice(0, 255),
    }));
    setIsComposeOpen(true);
  };

  const handleRecipientToggle = (type: 'contacts' | 'users' | 'subcontractors', id: number) => {
    const field = `recipient_${type}` as keyof typeof composeData;
    const current = composeData[field] as number[];

    setComposeData((prev) => ({
      ...prev,
      [field]: current.includes(id)
        ? current.filter((recipientId) => recipientId !== id)
        : [...current, id],
    }));
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isMessagesOpen && messages.length > 0) {
      scrollToBottom();
    }
  }, [messages, isMessagesOpen]);

  const refreshDelivery = async (message: CommunicationMessage) => {
    const response = await apiClient.get<CommunicationMessage[]>(
      `/api/jobs/${job.id}/communication-threads/${message.thread}/messages/`,
    );
    setMessages(response.data);
    setDeliveryResult(response.data.find((row) => row.id === message.id) || null);
  };
  const retryRecipient = async (message: CommunicationMessage, delivery: SMSDelivery) => {
    setRetrying(delivery.id);
    setSendError('');
    try {
      await postCommunication(`/api/jobs/${job.id}/sms-deliveries/${delivery.id}/retry/`, {});
      await refreshDelivery(message);
    } catch (error) {
      setSendError(communicationError(error));
    } finally {
      setRetrying(null);
    }
  };
  const renderDeliveries = (message: CommunicationMessage) => (
    <Stack spacing={1} sx={{ mt: 1 }}>
      {message.sms_deliveries?.map((delivery) => (
        <Box
          key={delivery.id}
          sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 'sm' }}
        >
          <Typography level="body-sm">
            {delivery.recipients.map((r) => r.name).join(', ')} ·{' '}
            {delivery.phone_number || 'No phone'} · {deliveryLabel(delivery.status)}
          </Typography>
          {delivery.error && (
            <Typography level="body-xs" color="danger">
              {delivery.error}
            </Typography>
          )}
          <Typography level="body-xs">
            {delivery.attempts.length} saved attempt{delivery.attempts.length === 1 ? '' : 's'}
          </Typography>
          {delivery.can_retry && (
            <Button
              size="sm"
              variant="outlined"
              loading={retrying === delivery.id}
              disabled={retrying !== null}
              onClick={() => retryRecipient(message, delivery)}
            >
              Retry this failed recipient
            </Button>
          )}
        </Box>
      ))}
    </Stack>
  );

  const recentThreads = isExpanded ? threads : threads.slice(0, 3);

  return (
    <Card variant="outlined">
      <CardContent>
        {sendError && (
          <Typography role="alert" color="danger">
            {sendError}
          </Typography>
        )}
        {deliveryResult && (
          <Card variant="soft" sx={{ mb: 2 }}>
            <Typography level="title-md">
              Job text: {deliveryLabel(deliveryResult.status)}
            </Typography>
            {renderDeliveries(deliveryResult)}
            <Button variant="plain" onClick={() => refreshDelivery(deliveryResult)}>
              Refresh delivery
            </Button>
          </Card>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography level="h4" sx={{ fontWeight: 600, fontSize: '20px' }}>
            Project Chat
          </Typography>
          <Button variant="soft" size="sm" startDecorator={<MessageIcon />} onClick={openCompose}>
            New Message
          </Button>
        </Box>

        {threads.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 4,
              px: 2,
              bgcolor: 'background.level1',
              borderRadius: 'sm',
            }}
          >
            <Typography level="title-lg" sx={{ color: 'text.secondary', mb: 1 }}>
              No conversations yet
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.tertiary', mb: 3 }}>
              Start a conversation with customers, team members, or subcontractors
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              startDecorator={<MessageIcon />}
              onClick={openCompose}
            >
              Send First Message
            </Button>
          </Box>
        ) : (
          <>
            <Stack spacing={2}>
              {recentThreads.map((thread) => (
                <Sheet
                  key={thread.id}
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 'sm',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'background.level1' },
                  }}
                  onClick={() => loadMessages(thread)}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      {getMessageTypeIcon(thread.thread_type)}
                      <Typography level="title-sm" sx={{ fontWeight: 600 }}>
                        {thread.subject}
                      </Typography>
                      {thread.unread_count > 0 && (
                        <Badge badgeContent={thread.unread_count} color="danger" size="sm" />
                      )}
                    </Box>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      {formatDistanceToNow(new Date(thread.last_activity_at), { addSuffix: true })}
                    </Typography>
                  </Box>

                  {thread.recent_message && (
                    <Box sx={{ mb: 2 }}>
                      <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 0.5 }}>
                        {thread.recent_message.sender.name}:
                      </Typography>
                      <Typography
                        level="body-sm"
                        sx={{
                          color: 'text.primary',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {thread.recent_message.content}
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {thread.participants.slice(0, 3).map((participant, index) => (
                      <Chip
                        key={`${participant.type}-${participant.id}`}
                        size="sm"
                        variant="soft"
                        startDecorator={getParticipantIcon(participant.type)}
                      >
                        {participant.name}
                      </Chip>
                    ))}
                    {thread.participants.length > 3 && (
                      <Chip size="sm" variant="soft">
                        +{thread.participants.length - 3} more
                      </Chip>
                    )}
                  </Box>
                </Sheet>
              ))}
            </Stack>

            {threads.length > 3 && (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Button
                  variant="plain"
                  size="sm"
                  endDecorator={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  onClick={() => setIsExpanded(!isExpanded)}
                >
                  {isExpanded ? 'Show Less' : `Show ${threads.length - 3} More Conversations`}
                </Button>
              </Box>
            )}
          </>
        )}

        {/* Compose Modal */}
        <Modal open={isComposeOpen} onClose={() => setIsComposeOpen(false)}>
          <ModalDialog size="lg" sx={{ maxWidth: 600, width: '90vw' }}>
            <ModalClose />
            <DialogTitle>Send New Message</DialogTitle>
            <DialogContent sx={{ overflowY: 'auto', minHeight: 0 }}>
              <Stack spacing={3}>
                <FormControl>
                  <FormLabel>Message Type</FormLabel>
                  <Select
                    value={composeData.message_type}
                    onChange={(_, value) => {
                      setRecipientOverrides({});
                      setComposeData((prev) => ({
                        ...prev,
                        message_type: value as 'email' | 'sms' | 'notification',
                      }));
                    }}
                  >
                    <Option value="email">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmailIcon size={18} />
                        Email
                      </Box>
                    </Option>
                    <Option value="sms">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SmsIcon size={18} />
                        SMS
                      </Box>
                    </Option>
                    <Option value="notification">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <NotificationsIcon size={18} />
                        ApexOS Notification
                      </Box>
                    </Option>
                  </Select>
                </FormControl>

                {composeData.message_type === 'email' && (
                  <FormControl>
                    <FormLabel>Subject</FormLabel>
                    <Input
                      placeholder="Email subject"
                      value={composeData.subject}
                      onChange={(e) =>
                        setComposeData((prev) => ({ ...prev, subject: e.target.value }))
                      }
                    />
                  </FormControl>
                )}

                <FormControl>
                  <FormLabel>Thread Subject (for new conversations)</FormLabel>
                  <Input
                    placeholder="Conversation topic"
                    value={composeData.thread_subject}
                    onChange={(e) =>
                      setComposeData((prev) => ({ ...prev, thread_subject: e.target.value }))
                    }
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Recipients</FormLabel>
                  <Box
                    sx={{
                      maxHeight: 200,
                      overflowY: 'auto',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 'sm',
                      p: 1,
                    }}
                  >
                    {participantsLoading ? (
                      <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                          Loading participants...
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        {participants.contacts?.length > 0 && (
                          <>
                            <Typography level="title-sm" sx={{ p: 1, fontWeight: 600 }}>
                              Customers
                            </Typography>
                            <List size="sm">
                              {participants.contacts.map((contact) => (
                                <ListItem key={`contact-${contact.id}`}>
                                  <Checkbox
                                    checked={composeData.recipient_contacts.includes(contact.id)}
                                    onChange={() => handleRecipientToggle('contacts', contact.id)}
                                  />
                                  <ListItemContent sx={{ ml: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <PersonIcon size={18} />
                                      <Typography level="body-sm">{contact.name}</Typography>
                                      {contact.email && (
                                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                          {contact.email}
                                        </Typography>
                                      )}
                                    </Box>
                                  </ListItemContent>
                                </ListItem>
                              ))}
                            </List>
                          </>
                        )}

                        {participants.users?.length > 0 && (
                          <>
                            <Typography level="title-sm" sx={{ p: 1, fontWeight: 600 }}>
                              Team Members
                            </Typography>
                            <List size="sm">
                              {participants.users.map((user) => (
                                <ListItem key={`user-${user.id}`}>
                                  <Checkbox
                                    checked={composeData.recipient_users.includes(user.id)}
                                    onChange={() => handleRecipientToggle('users', user.id)}
                                  />
                                  <ListItemContent sx={{ ml: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <PersonIcon size={18} />
                                      <Typography level="body-sm">{user.name}</Typography>
                                      {user.email && (
                                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                          {user.email}
                                        </Typography>
                                      )}
                                    </Box>
                                  </ListItemContent>
                                </ListItem>
                              ))}
                            </List>
                          </>
                        )}

                        {participants.subcontractors?.length > 0 && (
                          <>
                            <Typography level="title-sm" sx={{ p: 1, fontWeight: 600 }}>
                              Subcontractors
                            </Typography>
                            <List size="sm">
                              {participants.subcontractors.map((sub) => (
                                <ListItem key={`sub-${sub.id}`}>
                                  <Checkbox
                                    checked={composeData.recipient_subcontractors.includes(sub.id)}
                                    onChange={() => handleRecipientToggle('subcontractors', sub.id)}
                                  />
                                  <ListItemContent sx={{ ml: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <BusinessIcon size={18} />
                                      <Typography level="body-sm">{sub.name}</Typography>
                                      {sub.email && (
                                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                          {sub.email}
                                        </Typography>
                                      )}
                                    </Box>
                                  </ListItemContent>
                                </ListItem>
                              ))}
                            </List>
                          </>
                        )}

                        {/* Empty state when no participants available */}
                        {!participants.contacts?.length &&
                          !participants.users?.length &&
                          !participants.subcontractors?.length && (
                            <Box sx={{ textAlign: 'center', py: 2 }}>
                              <Typography level="body-sm" sx={{ color: 'text.tertiary', mb: 1 }}>
                                No participants available
                              </Typography>
                              <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                Add contacts to the job or create work orders with subcontractors to
                                see recipients here.
                              </Typography>
                            </Box>
                          )}
                      </>
                    )}
                  </Box>
                </FormControl>

                {composeData.message_type !== 'notification' && (
                  <Stack spacing={1.5}>
                    <Typography level="title-sm">Delivery details</Typography>
                    <Typography level="body-xs">
                      Edit an address for this message. Saved contact details stay the same.
                    </Typography>
                    {[
                      {
                        kind: 'contact',
                        people: participants.contacts.filter((p) =>
                          composeData.recipient_contacts.includes(p.id),
                        ),
                      },
                      {
                        kind: 'user',
                        people: participants.users.filter((p) =>
                          composeData.recipient_users.includes(p.id),
                        ),
                      },
                      {
                        kind: 'subcontractor',
                        people: participants.subcontractors.filter((p) =>
                          composeData.recipient_subcontractors.includes(p.id),
                        ),
                      },
                    ].flatMap((group) =>
                      group.people.map((person) => (
                        <FormControl required key={`${group.kind}:${person.id}`}>
                          <FormLabel>
                            {person.name} ·{' '}
                            {composeData.message_type === 'email' ? 'Email' : 'Phone'}
                          </FormLabel>
                          <Input
                            type={composeData.message_type === 'email' ? 'email' : 'tel'}
                            value={
                              recipientOverrides[`${group.kind}:${person.id}`] ??
                              (composeData.message_type === 'email'
                                ? person.email || ''
                                : person.phone || '')
                            }
                            onChange={(e) =>
                              setRecipientOverrides((previous) => ({
                                ...previous,
                                [`${group.kind}:${person.id}`]: e.target.value,
                              }))
                            }
                          />
                        </FormControl>
                      )),
                    )}
                  </Stack>
                )}

                {composeData.message_type === 'sms' && (
                  <>
                    <PhoneNumberSelector smsOnly value={smsSender} onChange={setSmsSender} />
                    <Typography level="body-sm">
                      Customers need project-update permission; contractors need coordination
                      permission; employees need staff-alert permission. Each destination receives a
                      separate text and charge.
                    </Typography>
                  </>
                )}
                <FormControl>
                  <FormLabel>Message</FormLabel>
                  <Textarea
                    minRows={4}
                    maxRows={8}
                    placeholder="Enter your message here..."
                    value={composeData.content}
                    onChange={(e) =>
                      setComposeData((prev) => ({ ...prev, content: e.target.value }))
                    }
                  />
                </FormControl>

                {composeData.message_type === 'sms' && (
                  <Stack spacing={1}>
                    {[
                      {
                        kind: 'contact',
                        people: participants.contacts.filter((p) =>
                          composeData.recipient_contacts.includes(p.id),
                        ),
                        purpose: 'customer_updates',
                      },
                      {
                        kind: 'subcontractor',
                        people: participants.subcontractors.filter((p) =>
                          composeData.recipient_subcontractors.includes(p.id),
                        ),
                        purpose: 'contractor_coordination',
                      },
                      {
                        kind: 'user',
                        people: participants.users.filter((p) =>
                          composeData.recipient_users.includes(p.id),
                        ),
                        purpose: 'staff_alerts',
                      },
                    ].flatMap((group) =>
                      group.people.map((person) => (
                        <Box key={`${group.purpose}:${person.id}`}>
                          <Typography level="body-xs">{person.name}</Typography>
                          <SMSMessagePreview
                            toNumber={
                              recipientOverrides[`${group.kind}:${person.id}`] ?? person.phone ?? ''
                            }
                            fromNumber={smsSender}
                            body={`[Job ${job.job_number}] ${composeData.content}`}
                            purpose={group.purpose}
                          />
                        </Box>
                      )),
                    )}
                  </Stack>
                )}
                {sendError && (
                  <Typography role="alert" color="danger">
                    {sendError}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button variant="plain" onClick={() => setIsComposeOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    loading={loading}
                    onClick={sendCommunication}
                    disabled={composeData.message_type === 'sms' && !smsSender}
                    startDecorator={<SendIcon />}
                  >
                    Send
                  </Button>
                </Box>
              </Stack>
            </DialogContent>
          </ModalDialog>
        </Modal>

        {/* Messages Modal */}
        <Modal open={isMessagesOpen} onClose={() => setIsMessagesOpen(false)}>
          <ModalDialog size="lg" sx={{ maxWidth: 800, width: '90vw', height: '80vh' }}>
            <ModalClose />
            <DialogTitle>
              {selectedThread?.subject}
              <Typography level="body-sm" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                {selectedThread?.message_count} messages
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ flex: 1, overflowY: 'auto', pr: 1 }}>
                <Stack spacing={2}>
                  {messages.map((message) => (
                    <Box
                      key={message.id}
                      sx={{
                        display: 'flex',
                        flexDirection: message.direction === 'outbound' ? 'row-reverse' : 'row',
                        gap: 2,
                      }}
                    >
                      <Avatar size="sm">{message.sender_info.name.charAt(0).toUpperCase()}</Avatar>
                      <Box
                        sx={{
                          maxWidth: '70%',
                          p: 2,
                          borderRadius: 'md',
                          bgcolor:
                            message.direction === 'outbound' ? 'primary.softBg' : 'neutral.softBg',
                          border: '1px solid',
                          borderColor:
                            message.direction === 'outbound'
                              ? 'primary.outlinedBorder'
                              : 'neutral.outlinedBorder',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                            {message.sender_info.name}
                          </Typography>
                          {getMessageTypeIcon(message.message_type)}
                          {getStatusIcon(message.status)}
                          <Typography level="body-xs">{deliveryLabel(message.status)}</Typography>
                          <Typography level="body-xs" sx={{ color: 'text.tertiary', ml: 'auto' }}>
                            {format(new Date(message.created_at), 'MMM d, h:mm a')}
                          </Typography>
                        </Box>
                        {message.subject && (
                          <Typography level="title-sm" sx={{ mb: 1, fontWeight: 600 }}>
                            {message.subject}
                          </Typography>
                        )}
                        <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>
                          {message.content}
                        </Typography>
                        {message.message_type === 'sms' && renderDeliveries(message)}
                        {message.recipients_info.length > 0 && (
                          <Box
                            sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}
                          >
                            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                              To: {message.recipients_info.map((r) => r.name).join(', ')}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  ))}
                  <div ref={messagesEndRef} />
                </Stack>
              </Box>
            </DialogContent>
          </ModalDialog>
        </Modal>
      </CardContent>
    </Card>
  );
};

export default JobCommunication;
